import React, { useEffect, useRef, useMemo } from 'react';
import { IconLightbulb, IconTarget } from './Icons';
import './WhyModal.css';

export default function WhyModal({ data, onClose }) {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const scoreObj = useMemo(() => {
    const score = data?.score;
    if (!score) return null;
    if (typeof score === 'object') {
      if (score.value || score.label) return score;
      return null;
    }
    if (typeof score === 'string' && score.trim()) {
      return {
        value: score,
        label: 'Overall Business Health Score',
        description: 'Calculated from verified government registrations and local market competition'
      };
    }
    if (typeof score === 'number') {
      return {
        value: `${score}/100`,
        label: 'Market Score',
        description: 'Based on local business activity and competition'
      };
    }
    return null;
  }, [data?.score]);

  if (!data) return null;

  const { title, subtitle, factors = [], takeaway, recommendation } = data;

  return (
    <div className="why-modal-backdrop" onClick={onClose}>
      <div 
        className="why-modal-container"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="why-modal-title"
      >
        <div className="why-modal-header">
          <div className="why-header-title-wrap">
            <span className="why-eyebrow">✦ WHY THIS MATTERS FOR YOUR BUSINESS</span>
            <h3 id="why-modal-title">{title}</h3>
            {subtitle && <p className="why-subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="why-close-btn" onClick={onClose} aria-label="Close explanation">✕</button>
        </div>

        <div className="why-modal-body">
          {scoreObj && (
            <div className="why-score-banner">
              <div className="why-score-number">{scoreObj.value}</div>
              <div className="why-score-info">
                <strong>{scoreObj.label}</strong>
                {scoreObj.description && <span>{scoreObj.description}</span>}
              </div>
            </div>
          )}

          {factors && factors.length > 0 && (
            <div className="why-factors-section">
              <h4 className="why-section-heading">Main Factors &amp; Details</h4>
              <div className="why-factors-list">
                {factors.map((factor, idx) => {
                  const name = factor.name || factor.title || factor.label || `Factor ${idx + 1}`;
                  const detail = factor.detail || factor.explanation || factor.desc || factor.description || '';
                  const impact = factor.impact || factor.impactLabel || factor.rating || 'Calculated';
                  
                  let impactType = factor.impactType;
                  if (!impactType) {
                    const impLower = String(impact).toLowerCase();
                    if (impLower.includes('positive') || impLower.includes('high') || impLower.includes('optimal') || impLower.includes('advantage')) {
                      impactType = 'positive';
                    } else if (impLower.includes('warning') || impLower.includes('moderate') || impLower.includes('medium')) {
                      impactType = 'warning';
                    } else if (impLower.includes('negative') || impLower.includes('low') || impLower.includes('tight') || impLower.includes('risk')) {
                      impactType = 'negative';
                    } else {
                      impactType = 'neutral';
                    }
                  }

                  return (
                    <div key={idx} className="why-factor-item">
                      <div className="why-factor-top">
                        <span className="why-factor-name">{name}</span>
                        <span className={`why-factor-impact ${impactType}`}>{impact}</span>
                      </div>
                      {factor.weight != null && (
                        <div className="why-factor-bar-bg">
                          <div className="why-factor-bar-fill" style={{ width: `${Math.min(100, Math.max(0, factor.weight))}%` }} />
                        </div>
                      )}
                      {detail && <p className="why-factor-detail">{detail}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {takeaway && (
            <div className="why-takeaway-card">
              <span className="why-takeaway-title">
                <IconLightbulb width={15} height={15} style={{ marginRight: '6px', color: '#f59e0b' }} />
                What This Means For You
              </span>
              <p className="why-takeaway-text">{takeaway}</p>
            </div>
          )}

          {recommendation && (
            <div className="why-recommendation-card">
              <span className="why-rec-title">
                <IconTarget width={15} height={15} style={{ marginRight: '6px', color: '#2563eb' }} />
                What You Should Do Next
              </span>
              <p className="why-rec-text">{recommendation}</p>
            </div>
          )}
        </div>

        <div className="why-modal-footer">
          <span className="why-footer-note">Verified using official government business records across India</span>
          <button type="button" className="why-done-btn" onClick={onClose}>Close Window</button>
        </div>
      </div>
    </div>
  );
}
